import {motion, useMotionValue, useSpring, useTransform} from "framer-motion";
import {ArrowDown, FileDown} from "lucide-react";
import PhoneMockup from "./PhoneMockup";
import {EASE} from "./Reveal";
import scrollToId from "../lib/scrollToId";

const MaskedLine = ({children, delay = 0, className = ""}) => (
    <span className={`block overflow-hidden ${className}`}>
    <motion.span
        className="block"
        initial={{y: "110%"}}
        animate={{y: 0}}
        transition={{duration: 1.2, ease: EASE, delay}}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), {stiffness: 60, damping: 18});
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), {stiffness: 60, damping: 18});
    const drift = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), {stiffness: 50, damping: 20});

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            data-testid="hero-section"
            onMouseMove={onMove}
            onMouseLeave={() => {
                mx.set(0);
                my.set(0);
            }}
            className="relative flex min-h-screen items-center overflow-hidden pt-24"
        >
            <div
                className="pointer-events-none absolute inset-0"
                style={{background: "radial-gradient(ellipse 60% 50% at 75% 35%, rgba(59,130,246,0.08), transparent 65%)"}}
                aria-hidden="true"
            />
            <div
                className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-20 lg:grid-cols-12 lg:px-10">
                <div className="lg:col-span-7">
                    <MaskedLine delay={0.15}>
            <span className="font-mono text-xs tracking-[0.35em] text-accent" data-testid="hero-kicker">
              {"// MOBILE DEVELOPER — FLUTTER & DART — iOS & ANDROID"}
            </span>
                    </MaskedLine>
                    <h1 className="mt-6 font-display font-extrabold leading-[0.92] tracking-tight text-white"
                        data-testid="hero-name">
                        <MaskedLine delay={0.3} className="text-[clamp(2.5rem,7vw,5.6rem)]">DMITRIY</MaskedLine>
                        <MaskedLine delay={0.42} className="text-[clamp(2.5rem,7vw,5.6rem)]">
                            TRAPEZNIKOV
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{opacity: 0, y: 24}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.9, ease: EASE, delay: 0.85}}
                        className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg"
                        data-testid="hero-intro"
                    >
                        Mobile developer with 5+ years of experience building and improving mobile apps.
                        My recent work spans e-commerce, subscription products, and enterprise tools, with a focus on
                        architecture, payments, and platform integrations.
                    </motion.p>
                    <motion.div
                        initial={{opacity: 0, y: 24}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.9, ease: EASE, delay: 1.0}}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="view-projects-btn"
                            onClick={() => scrollToId("projects")}
                            className="group inline-flex items-center gap-3 rounded-full bg-accent px-7 py-3.5 font-mono text-xs font-medium tracking-[0.2em] text-white transition-all duration-300 hover:bg-blue-500 hover:shadow-[0_0_35px_rgba(59,130,246,0.45)]"
                        >
                            VIEW PROJECTS
                            <ArrowDown
                                className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5"/>
                        </button>
                        <a
                            data-testid="download-cv-btn"
                            href="/documents/dmitriy-trapeznikov-cv.pdf"
                            download="Dmitriy-Trapeznikov-CV.pdf"
                            className="group inline-flex items-center gap-3 rounded-full border border-white/10 px-7 py-3.5 font-mono text-xs font-medium tracking-[0.2em] text-zinc-300 transition-all duration-300 hover:border-accent/40 hover:text-white"
                        >
                            DOWNLOAD CV
                            <FileDown
                                className="h-4 w-4 text-zinc-500 transition-colors duration-300 group-hover:text-accent"/>
                        </a>
                    </motion.div>
                </div>

                <div className="relative hidden justify-center lg:col-span-5 lg:flex" style={{perspective: 1200}}>
                    <motion.div
                        initial={{opacity: 0, y: 60}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 1.3, ease: EASE, delay: 0.55}}
                        style={{rotateX, rotateY, x: drift, transformStyle: "preserve-3d"}}
                    >
                        <PhoneMockup variant="pulse"/>
                    </motion.div>
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 1, delay: 1.4}}
                        className="absolute -left-2 top-10 rounded-lg border border-white/10 bg-surface/80 px-4 py-3 backdrop-blur-md"
                    >
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">SHIPPED APPS</div>
                        <div className="font-display text-2xl font-semibold text-white"
                             data-testid="hero-stat-apps">9<span className="text-accent">+</span></div>
                    </motion.div>
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 1, delay: 1.6}}
                        className="absolute -right-2 bottom-16 rounded-lg border border-white/10 bg-surface/80 px-4 py-3 backdrop-blur-md"
                    >
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">APP STORE RATING</div>
                        <div className="font-display text-2xl font-semibold text-white"
                             data-testid="hero-stat-rating">4.8<span className="text-accent">★</span></div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{duration: 1, delay: 1.8}}
                className="absolute bottom-8 left-6 flex items-center gap-3 lg:left-10"
            >
                <div className="h-px w-12 bg-zinc-700"/>
                <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-600">SCROLL</span>
            </motion.div>
        </section>
    );
}
